// ============================================================================
// G06 Multi-IGCC/RCC page
// ============================================================================
import React, { useCallback, useMemo, useRef, useState, startTransition } from "react";
import axios from "axios";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Collapse,
    Divider,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { API_BASE_URL } from "../../utils/constants";
import { describeG06Status, classifyG06Error, errorCategoryLabel } from "../../utils/g06ErrorMessages";
import { G06BlockDataGrid } from "../Common/G06BlockDataGrid";

// ----- Constants -----
const REF_CAL_LEVELS = 10;
const MASTER_ALU_LEVELS = 10;
const RCC_ALU_LEVELS = 10;
const IGCC_LEVELS = 3;
const DEFAULT_UNKNOWN_ROWS = 20;
const MIN_UNKNOWN_ROWS = 1;
const MAX_UNKNOWN_ROWS = 600;
const DEFAULT_ASSAY_COUNT = 10;
const LABEL_COL_WIDTH = 70;    // fixed leftmost label column
const MIN_ASSAY_COL_WIDTH = 90; // minimum before horizontal scroll kicks in

const API_BASE = (API_BASE_URL || "").replace(/\/+$/, "") + "/";
const RAWCURVE_POOLED_URL = `${API_BASE}api/v1/rawcurve/fitpooled`;

const APP_VERSION = "1.0.0";
const MATH_LIB_VERSION = "G06 v5.1 (1997-03-14, GAPS-derived protein5 fit)";

const parseMaybeNumber = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const s = String(v).trim();
    if (s === "") return null;
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
};

const formatWithRounding = (v, sig, prec) => {
    if (v === null || v === undefined || v === "") return "";
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    if (n === 0) return n.toFixed(prec);
    const magnitude = Math.floor(Math.log10(Math.abs(n))) + 1;
    const scale = Math.pow(10, sig - magnitude);
    return (Math.round(n * scale) / scale).toFixed(prec);
};

// Per-mode result shape stored inside each assay
const makeEmptyResult = (unknownRows) => ({
    params: { a: "", b: "", c: "", d: "", n: "" },
    fitFactor: "",
    shapeFactor: "",
    igccApplied: false,
    fittedDoseFromAlu: Array(unknownRows).fill(""),
    fittedAluFromDose: Array(unknownRows).fill(""),
    curvePoints: [],
    pooledSignals: {},
    status: "",          // "ok" | "error" | "warning" | ""
    statusCode: null,    // raw M_RTN_CODE integer from backend
    errorCategory: ""    // "error1" | "error2" | "error3" | "error4" | "warning" | "ok" | ""
});

const makeEmptyAssay = (idx, unknownRows = DEFAULT_UNKNOWN_ROWS) => ({
    name: `Assay ${idx + 1}`,
    refCalConc: Array(REF_CAL_LEVELS).fill(""),
    masterAlu: Array(MASTER_ALU_LEVELS).fill(""),
    rccAlu: Array(RCC_ALU_LEVELS).fill(""),
    igccAlu: Array(IGCC_LEVELS).fill(""),
    igccConc: Array(IGCC_LEVELS).fill(""),
    unknownAlu: Array(unknownRows).fill(""),
    unknownConc: Array(unknownRows).fill(""),
    rccResult: makeEmptyResult(unknownRows),
    igccResult: makeEmptyResult(unknownRows)
});

// Pure builder: turn a backend response into a result object. Kept outside
// the component so batch handlers can build many results without touching
// component state, then apply them all in a single setState.
const buildResult = (assay, data, sig, prec, igccPairsPresent = false) => {
    const fmt = v => formatWithRounding(v, sig, prec);
    const rawParams = Array.isArray(data?.params) && data.params.length >= 5 ? data.params : null;
    const igccApplied = !!data?.igcc_applied;
    const statusCode = data?.status ?? null;
    const errorCategory = classifyG06Error(statusCode, igccApplied, igccPairsPresent);
    // When IGCC pairs were sent but not applied, do not leak the plain RCC
    // curve params into the IGCC result column � show empty instead.
    const igccFailed = igccPairsPresent && !igccApplied;
    const params = (!igccFailed && rawParams) ? rawParams : null;
    return {
        params: params ? { a: fmt(params[0]), b: fmt(params[1]), c: fmt(params[2]), d: fmt(params[3]), n: fmt(params[4]) } : { a: "", b: "", c: "", d: "", n: "" },
        fitFactor: igccFailed ? "" : (data?.fit_factor != null ? fmt(data.fit_factor) : ""),
        shapeFactor: igccFailed ? "" : ((igccApplied && data?.igcc_shape_factor != null) ? fmt(data.igcc_shape_factor) : (data?.shape_factor != null ? fmt(data.shape_factor) : "")),
        igccApplied,
        curvePoints: Array.isArray(data?.curve_points) ? data.curve_points.map(p => ({ x: Number(p.x), y: Number(p.y) })).filter(p => Number.isFinite(p.x) && Number.isFinite(p.y)) : [],
        pooledSignals: data?.pooled_signals || {},
        status: (errorCategory === "ok" || errorCategory === "warning") ? "ok" : "error",
        statusCode,
        errorCategory,
        fittedDoseFromAlu: assay.unknownAlu.map((src, i) => {
            if (parseMaybeNumber(src) === null) return "";
            const v = Array.isArray(data?.unknown_doses_from_alu) ? data.unknown_doses_from_alu[i] : undefined;
            return (v == null || !Number.isFinite(Number(v))) ? "" : fmt(v);
        }),
        fittedAluFromDose: assay.unknownConc.map((src, i) => {
            if (parseMaybeNumber(src) === null) return "";
            const v = Array.isArray(data?.unknown_alus_from_dose) ? data.unknown_alus_from_dose[i] : undefined;
            return (v == null || !Number.isFinite(Number(v))) ? "" : fmt(v);
        })
    };
};

// ---- AssaySectionGrid ----
// Accepts assayNames (string[]) and sectionValues (string[][]) instead of
// the full assays array so its memos only re-run when the specific section's
// own data changes, not when results or other sections update.
const AssaySectionGrid = React.memo(({ sectionKey, levels, assayNames, sectionValues, rowLabel, labelTitle, onCellsChange, colWidth }) => {
    const columnDefs = useMemo(() => {
        const w = colWidth || MIN_ASSAY_COL_WIDTH;
        const cols = [{ key: "__label", name: labelTitle, width: LABEL_COL_WIDTH, editable: false }];
        for (let ai = 0; ai < assayNames.length; ai++) {
            cols.push({ key: `a${ai}`, name: assayNames[ai], width: w });
        }
        return cols;
    }, [assayNames, labelTitle, colWidth]);

    const rows = useMemo(() => Array.from({ length: levels }, (_, rowIdx) => {
        const r = { id: rowIdx, __label: `${rowLabel} ${rowIdx + 1}` };
        for (let ai = 0; ai < sectionValues.length; ai++) {
            r[`a${ai}`] = sectionValues[ai][rowIdx] ?? "";
        }
        return r;
    }), [sectionValues, levels, rowLabel]);

    const handleDataChange = useCallback((newRows) => {
        const changes = [];
        for (let rowIdx = 0; rowIdx < levels; rowIdx++) {
            const newRow = newRows[rowIdx];
            if (!newRow) continue;
            for (let ai = 0; ai < sectionValues.length; ai++) {
                const key = `a${ai}`;
                const prevVal = sectionValues[ai][rowIdx] ?? "";
                const nextVal = newRow[key] ?? "";
                if (String(prevVal) !== String(nextVal)) {
                    changes.push({ assayIdx: ai, rowIdx, value: nextVal });
                }
            }
        }
        if (changes.length > 0) onCellsChange(sectionKey, changes);
    }, [sectionValues, levels, sectionKey, onCellsChange]);

    return (
        <Box sx={{ overflowX: "auto", width: "100%" }}>
            <G06BlockDataGrid columnDefs={columnDefs} data={rows} rowCount={levels} onDataChange={handleDataChange} />
        </Box>
    );
});

// ---- ReadonlyCellGrid ----
// Plain divs (NOT MUI TextField) - read-only cells need no input element.
// Using TextField here mounts thousands of MUI InputBase components whose
// passive mount effects call setState, tripping React's update-depth guard
// when many assays re-render during batch processing.
const ReadonlyCellGrid = React.memo(({ values, count }) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
        {Array.from({ length: count }, (_, i) => (
            <Box key={i} sx={{ height: 22, lineHeight: "22px", px: 0.75, fontSize: 11, textAlign: "right", backgroundColor: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 0.5, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                {values[i] ?? ""}
            </Box>
        ))}
    </Box>
));

// ---- OutputPanel ----
const OutputPanel = React.memo(({ assays, resultKey, label, unknownRowCount, activeAssay }) => {
    const isIgcc = resultKey === "igccResult";
    return (
        <Paper sx={{ p: 2, mb: 2, overflowX: "auto" }}>
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 180, position: "sticky", left: 0, backgroundColor: "white", zIndex: 1 }}>
                    <Box sx={{ height: 22, fontSize: 10, fontWeight: 700, color: "text.secondary" }}>Return Code</Box>
                    <Box sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>Status</Box>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{label} Curve Params</Typography>
                    {["a", "b", "c", "d", "n"].map(k => <Box key={k} sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>{k}</Box>)}
                    <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>Fit Stats</Typography>
                    {["Fit Factor", "Shape Factor", isIgcc ? "IGCC Applied" : ""].filter(Boolean).map(k => (
                        <Box key={k} sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>{k}</Box>
                    ))}
                    <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>Fitted Dose (ALU -&gt; Dose)</Typography>
                    {Array.from({ length: unknownRowCount }, (_, i) => <Box key={i} sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>#{i + 1}</Box>)}
                    <Typography variant="caption" sx={{ fontWeight: 600, mt: 0.5 }}>Fitted ALU (Conc -&gt; ALU)</Typography>
                    {Array.from({ length: unknownRowCount }, (_, i) => <Box key={i} sx={{ height: 22, fontSize: 10, color: "text.secondary" }}>#{i + 1}</Box>)}
                </Box>
                {assays.map((a, ai) => {
                    const res = a[resultKey];
                    const cat = res.errorCategory || "";
                    const isErr = res.status === "error" || ["error1","error2","error3","error4"].includes(cat);
                    const isWarn = cat === "warning";
                    const statBg = isErr && !isWarn
                        ? "rgba(198,40,40,0.08)"
                        : isWarn
                            ? "rgba(237,108,2,0.08)"
                            : res.status === "ok"
                                ? "rgba(46,125,50,0.08)"
                                : undefined;
                    const catColor = ["error1","error2","error3","error4"].includes(cat)
                        ? "#c62828"
                        : cat === "warning"
                            ? "#e65100"
                            : cat === "ok"
                                ? "#2e7d32"
                                : res.status === ""
                                    ? "#999"
                                    : "#555";
                    const catLabel = res.status === ""
                        ? "(not run)"
                        : cat ? errorCategoryLabel(cat) : (res.status === "ok" ? "OK" : "Error");
                    const fitStatValues = isIgcc
                        ? [res.fitFactor, res.shapeFactor, res.igccApplied ? "Yes" : (res.status === "ok" ? "No" : "")]
                        : [res.fitFactor, res.shapeFactor];
                    return (
                        <Box key={ai} sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 110, borderLeft: "1px solid #e0e0e0", pl: 0.5, backgroundColor: ai === activeAssay ? "rgba(25,118,210,0.04)" : statBg }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, textAlign: "center" }}>{a.name}</Typography>
                            {/* Return code */}
                            <Box sx={{ height: 22, lineHeight: "22px", px: 0.75, fontSize: 10, textAlign: "center", fontFamily: "monospace", backgroundColor: "#f5f5f5", border: "1px solid #e0e0e0", borderRadius: 0.5 }}>
                                {res.statusCode != null ? `[${res.statusCode}]` : (res.status === "" ? "" : "[1]")}
                            </Box>
                            {/* Error category label */}
                            <Box sx={{ height: 22, lineHeight: "22px", px: 0.5, fontSize: 10, textAlign: "center", fontWeight: 600, color: catColor, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}
                                title={catLabel}>
                                {catLabel}
                            </Box>
                            <Box sx={{ height: 18 }} />
                            <ReadonlyCellGrid values={[res.params.a, res.params.b, res.params.c, res.params.d, res.params.n]} count={5} />
                            <Box sx={{ height: 18 }} />
                            <ReadonlyCellGrid values={fitStatValues} count={isIgcc ? 3 : 2} />
                            <Box sx={{ height: 18 }} />
                            <ReadonlyCellGrid values={res.fittedDoseFromAlu} count={unknownRowCount} />
                            <Box sx={{ height: 18 }} />
                            <ReadonlyCellGrid values={res.fittedAluFromDose} count={unknownRowCount} />
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
});

// ---- Main component ----
const G06MultiAssay = () => {
    const [operationStatus, setOperationStatus] = useState({ message: "Ready", busy: false, level: "info", at: new Date() });
    const startOperation = useCallback((msg) => setOperationStatus({ message: msg, busy: true, level: "info", at: new Date() }), []);
    const finishOperation = useCallback((msg, level = "success") => setOperationStatus({ message: msg, busy: false, level, at: new Date() }), []);

    const [unknownRowCount, setUnknownRowCount] = useState(DEFAULT_UNKNOWN_ROWS);
    const [unknownRowDraft, setUnknownRowDraft] = useState(String(DEFAULT_UNKNOWN_ROWS));
    const [assays, setAssays] = useState(() => Array.from({ length: DEFAULT_ASSAY_COUNT }, (_, i) => makeEmptyAssay(i)));
    const [assayCountDraft, setAssayCountDraft] = useState(String(DEFAULT_ASSAY_COUNT));
    const [activeAssay, setActiveAssay] = useState(0);
    const [chartAssay, setChartAssay] = useState(0);
    const [activeAssaySearchOpen, setActiveAssaySearchOpen] = useState(false);
    const [chartAssaySearchOpen, setChartAssaySearchOpen] = useState(false);
    const [activeAssaySearch, setActiveAssaySearch] = useState("");
    const [chartAssaySearch, setChartAssaySearch] = useState("");

    const [rounding, setRounding] = useState({ significantDigits: 4, precisionDigits: 3 });

    const [inputsOpen, setInputsOpen] = useState(true);
    const [rccOutputOpen, setRccOutputOpen] = useState(true);
    const [igccOutputOpen, setIgccOutputOpen] = useState(true);
    const [chartOpen, setChartOpen] = useState(true);
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [chartTitle, setChartTitle] = useState("Dose Curve Response");
    const [xAxisLabel, setXAxisLabel] = useState("Concentration");
    const [yAxisLabel, setYAxisLabel] = useState("Signal (ALU)");

    const fileInputRef = useRef(null);
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    // Observe the inputs Paper width. The Paper's width is page-driven (the
    // grids live inside an overflow:auto box), so this never feeds back from
    // column width changes - no resize loop.
    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        let last = 0;
        const obs = new ResizeObserver(entries => {
            const w = Math.floor(entries[0]?.contentRect?.width || 0);
            if (Math.abs(w - last) > 1) { last = w; setContainerWidth(w); }
        });
        obs.observe(el);
        return () => obs.disconnect();
    }, [inputsOpen]);

    // colWidth: <=20 assays fill the section evenly (min 90px); >20 lock to
    // 90px and let the section scroll horizontally.
    const colWidth = useMemo(() => {
        if (assays.length > 20 || containerWidth <= 0) return MIN_ASSAY_COL_WIDTH;
        const available = containerWidth - LABEL_COL_WIDTH - 24;
        return Math.max(MIN_ASSAY_COL_WIDTH, Math.floor(available / assays.length));
    }, [containerWidth, assays.length]);

    // Memoized slices - use assayCount to invalidate only when array length
    // changes or a cell is edited (assays ref changes on edit).
    const assayNames        = useMemo(() => assays.map(a => a.name),        [assays]); // eslint-disable-line react-hooks/exhaustive-deps
    const refCalConcValues  = useMemo(() => assays.map(a => a.refCalConc),  [assays]); // eslint-disable-line react-hooks/exhaustive-deps
    const masterAluValues   = useMemo(() => assays.map(a => a.masterAlu),   [assays]); // eslint-disable-line react-hooks/exhaustive-deps
    const rccAluValues      = useMemo(() => assays.map(a => a.rccAlu),      [assays]); // eslint-disable-line react-hooks/exhaustive-deps
    const igccAluValues     = useMemo(() => assays.map(a => a.igccAlu),     [assays]); // eslint-disable-line react-hooks/exhaustive-deps
    const igccConcValues    = useMemo(() => assays.map(a => a.igccConc),    [assays]); // eslint-disable-line react-hooks/exhaustive-deps
    const unknownAluValues  = useMemo(() => assays.map(a => a.unknownAlu),  [assays]); // eslint-disable-line react-hooks/exhaustive-deps
    const unknownConcValues = useMemo(() => assays.map(a => a.unknownConc), [assays]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clamp activeAssay when assays shrink (only on length decrease).
    const assayCount = assays.length;
    React.useEffect(() => {
        setActiveAssay(prev => prev >= assayCount ? Math.max(0, assayCount - 1) : prev);
    }, [assayCount]);
    React.useEffect(() => {
        setChartAssay(prev => prev >= assayCount ? Math.max(0, assayCount - 1) : prev);
    }, [assayCount]);
    React.useEffect(() => {
        setAssayCountDraft(String(assayCount));
    }, [assayCount]);

    // ---- Assay count controls ----
    const addAssays = useCallback((n) => {
        startTransition(() => {
            setAssays(prev => {
                const next = [...prev];
                for (let i = 0; i < n; i++) next.push(makeEmptyAssay(next.length, unknownRowCount));
                return next;
            });
        });
        finishOperation(`Added ${n} assay${n > 1 ? "s" : ""}.`, "info");
    }, [unknownRowCount, finishOperation]);

    const removeAssays = useCallback((n) => {
        let removed = 0;
        setAssays(prev => {
            const next = prev.slice(0, Math.max(1, prev.length - n));
            removed = prev.length - next.length;
            return next;
        });
        if (removed > 0) finishOperation(`Removed ${removed} assay${removed > 1 ? "s" : ""}.`, "info");
    }, [finishOperation]);

    const setAssayCountDirect = useCallback((count) => {
        const newVal = Math.max(1, Math.min(200, count));
        setAssayCountDraft(String(newVal));
    }, []);

    const confirmAssayCount = useCallback(() => {
        const target = Math.max(1, Math.min(200, Number(assayCountDraft) || assays.length));
        if (target > assays.length) addAssays(target - assays.length);
        else if (target < assays.length) removeAssays(assays.length - target);
    }, [assayCountDraft, assays.length, addAssays, removeAssays]);

    // ---- Unknown row controls ----
    const changeUnknownRows = useCallback((delta) => {
        startTransition(() => {
            const newVal = Math.max(MIN_UNKNOWN_ROWS, Math.min(MAX_UNKNOWN_ROWS, unknownRowCount + delta));
            setUnknownRowCount(newVal);
            setUnknownRowDraft(String(newVal));
        });
    }, [unknownRowCount]);

    const setUnknownRowsDirect = useCallback((count) => {
        const newVal = Math.max(MIN_UNKNOWN_ROWS, Math.min(MAX_UNKNOWN_ROWS, count));
        setUnknownRowDraft(String(newVal));
    }, []);

    const confirmUnknownRows = useCallback(() => {
        const newVal = Math.max(MIN_UNKNOWN_ROWS, Math.min(MAX_UNKNOWN_ROWS, Number(unknownRowDraft) || unknownRowCount));
        startTransition(() => {
            setUnknownRowCount(newVal);
            setUnknownRowDraft(String(newVal));
        });
    }, [unknownRowDraft, unknownRowCount]);

    // Filter assays based on search term
    const filteredActiveAssays = useMemo(() => {
        if (!activeAssaySearch.trim()) return assays.map((a, i) => ({ idx: i, name: a.name }));
        const search = activeAssaySearch.toLowerCase();
        return assays.map((a, i) => ({ idx: i, name: a.name }))
            .filter(a => a.name.toLowerCase().includes(search) || String(a.idx + 1).includes(search));
    }, [assays, activeAssaySearch]);

    const filteredChartAssays = useMemo(() => {
        if (!chartAssaySearch.trim()) return assays.map((a, i) => ({ idx: i, name: a.name, hasResults: a.rccResult.status !== "" || a.igccResult.status !== "" }));
        const search = chartAssaySearch.toLowerCase();
        return assays.map((a, i) => ({ idx: i, name: a.name, hasResults: a.rccResult.status !== "" || a.igccResult.status !== "" }))
            .filter(a => a.name.toLowerCase().includes(search) || String(a.idx + 1).includes(search));
    }, [assays, chartAssaySearch]);

    // ---- Cell editor ----
    const setAssayName = useCallback((ai, value) => {
        setAssays(prev => { const copy = [...prev]; copy[ai] = { ...copy[ai], name: value }; return copy; });
    }, []);

    const setAssayCellsBatch = useCallback((sectionKey, changes) => {
        if (!changes || changes.length === 0) return;
        setAssays(prev => {
            const copy = [...prev];
            const byAssay = new Map();
            for (const ch of changes) {
                if (!byAssay.has(ch.assayIdx)) byAssay.set(ch.assayIdx, []);
                byAssay.get(ch.assayIdx).push(ch);
            }
            for (const [ai, list] of byAssay) {
                const a = { ...copy[ai] };
                const arr = [...a[sectionKey]];
                for (const { rowIdx, value } of list) {
                    while (arr.length <= rowIdx) arr.push(""); // pad if row added beyond current length
                    arr[rowIdx] = value;
                }
                a[sectionKey] = arr;
                copy[ai] = a;
            }
            return copy;
        });
    }, []);

    const handleClearAll = () => {
        setAssays(Array.from({ length: assays.length }, (_, i) => makeEmptyAssay(i, unknownRowCount)));
        finishOperation("All assay data cleared.", "info");
    };

    // ---- Core fit ----
    const runFitForAssay = useCallback(async (assayIdx, mode) => {
        const assay = assays[assayIdx];
        if (!assay) return { ok: false, reason: "missing assay" };

        const referenceColumn = mode === "igcc" ? assay.masterAlu : assay.rccAlu;
        const referenceLabel = mode === "igcc" ? "Master Curve ALU" : "Reference Calibrator ALU";

        // ---- Error 1: detect non-numeric entries in Ref Cal Conc or ALU ----
        // (ERROR_MESSAGE_B / ERROR_MESSAGE_D: "Non-numeric entries in dose or ALU",
        //  "Input is not a number")
        for (let i = 0; i < REF_CAL_LEVELS; i++) {
            const rawC = assay.refCalConc[i];
            const rawY = referenceColumn[i];
            const hasC = rawC !== "" && rawC !== null && rawC !== undefined;
            const hasY = rawY !== "" && rawY !== null && rawY !== undefined;
            if (hasC && parseMaybeNumber(rawC) === null)
                return { ok: false, reason: `Error 1: Non-numeric entry in Ref Cal Conc at level ${i + 1} ("${rawC}"). All concentration values must be numbers.`, statusCode: null, errorCategory: "error1" };
            if (hasY && parseMaybeNumber(rawY) === null)
                return { ok: false, reason: `Error 1: Non-numeric entry in ${referenceLabel} at level ${i + 1} ("${rawY}"). All signal values must be numbers.`, statusCode: null, errorCategory: "error1" };
        }

        const concs = [], refSig = [];
        for (let i = 0; i < REF_CAL_LEVELS; i++) {
            const c = parseMaybeNumber(assay.refCalConc[i]);
            const y = parseMaybeNumber(referenceColumn[i]);
            // ---- Error 1: one of the pair is filled but the other is empty ----
            // (ERROR_MESSAGE_A: "Unequal number of dose/ALU entries")
            if ((c !== null) !== (y !== null))
                return { ok: false, reason: `Error 1: Unequal dose/ALU entries at level ${i + 1} � fill both Ref Cal Conc and ${referenceLabel}, or clear both.`, statusCode: null, errorCategory: "error1" };
            if (c !== null && y !== null) { concs.push(c); refSig.push(y); }
        }

        // ---- Error 1: fewer than 5 valid levels ----
        if (concs.length < 5) return { ok: false, reason: `Error 1: Only ${concs.length} valid ${referenceLabel} rows � at least 5 complete levels are required.`, statusCode: null, errorCategory: "error1" };

        // ---- Error 1: concentration not strictly increasing ----
        // (ERROR_MESSAGE_C: "Trend mismatch with RCC/MGCC")
        for (let i = 1; i < concs.length; i++) {
            if (!(concs[i] > concs[i - 1]))
                return { ok: false, reason: `Error 1: Ref Cal Conc is not strictly increasing � level ${i + 1} (${concs[i]}) must be greater than level ${i} (${concs[i - 1]}).`, statusCode: null, errorCategory: "error1" };
        }

        // ---- Error 2: signals not monotonic (pre-check before sending) ----
        // (ERROR_MESSAGE_G: "Fit Failed" � catch the obvious case client-side)
        {
            let sawUp = false, sawDown = false;
            for (let i = 1; i < refSig.length; i++) {
                if (refSig[i] > refSig[i - 1]) sawUp = true;
                else if (refSig[i] < refSig[i - 1]) sawDown = true;
                if (sawUp && sawDown)
                    return { ok: false, reason: `Error 2: ${referenceLabel} signals are not monotonic at level ${i + 1} (${refSig[i]} after ${refSig[i - 1]}). Signals must run consistently ascending or descending.`, statusCode: 0, errorCategory: "error2" };
            }
        }

        // ---- Error 1: signal values too large ----
        // (ERROR_MESSAGE_E: "Numeric dose/ALU value(s) too large", upper limit 1e11)
        const ALU_MAX = 1e11;
        for (let i = 0; i < refSig.length; i++) {
            if (refSig[i] > ALU_MAX)
                return { ok: false, reason: `Error 1: ${referenceLabel} value at level ${i + 1} (${refSig[i]}) exceeds the maximum allowed signal of 1e11 ALU.`, statusCode: null, errorCategory: "error1" };
        }

        // ---- IGCC-specific validation ----
        const igccPairs = [];
        if (mode === "igcc") {
            for (let i = 0; i < IGCC_LEVELS; i++) {
                const rawA = assay.igccAlu[i];
                const rawD = assay.igccConc[i];
                const hasA = rawA !== "" && rawA !== null && rawA !== undefined;
                const hasD = rawD !== "" && rawD !== null && rawD !== undefined;

                // ERROR_MESSAGE_B / ERROR_MESSAGE_D: non-numeric entries
                if (hasA && parseMaybeNumber(rawA) === null)
                    return { ok: false, reason: `Error 3: Non-numeric entry in IGCC Calibrator ALU at row ${i + 1} ("${rawA}"). All IGCC values must be numbers.`, statusCode: null, errorCategory: "error3" };
                if (hasD && parseMaybeNumber(rawD) === null)
                    return { ok: false, reason: `Error 3: Non-numeric entry in IGCC Calibrator Conc at row ${i + 1} ("${rawD}"). All IGCC values must be numbers.`, statusCode: null, errorCategory: "error3" };

                const a = parseMaybeNumber(rawA);
                const d = parseMaybeNumber(rawD);

                // ERROR_MESSAGE_A: unequal number of dose/ALU entries
                if ((a !== null) !== (d !== null))
                    return { ok: false, reason: `Error 3: Unequal IGCC entries at row ${i + 1} � fill both ALU and Conc, or clear both.`, statusCode: null, errorCategory: "error3" };

                if (a !== null && d !== null) {
                    // ERROR_MESSAGE_E / ERROR_MESSAGE_F: value too large
                    if (a > ALU_MAX)
                        return { ok: false, reason: `Error 3: IGCC Calibrator ALU at row ${i + 1} (${a}) exceeds the maximum allowed value of 1e11 ALU.`, statusCode: null, errorCategory: "error3" };
                    igccPairs.push({ alu: a, dose: d });
                }
            }

            // ERROR_MESSAGE_C at IGCC level: conc values must be strictly increasing
            // Out-of-order IGCC concs cause the IGCC curve fit to fail (Error 4)
            for (let i = 1; i < igccPairs.length; i++) {
                if (!(igccPairs[i].dose > igccPairs[i - 1].dose))
                    return { ok: false, reason: `Error 4: IGCC Calibrator Conc is not strictly increasing � row ${i + 1} (${igccPairs[i].dose}) must be greater than row ${i} (${igccPairs[i - 1].dose}). Re-order the IGCC calibrator rows.`, statusCode: null, errorCategory: "error4" };
            }
        }

        const unknownAlus = assay.unknownAlu.map(v => { const n = parseMaybeNumber(v); return n === null ? NaN : n; });
        const unknownDoses = assay.unknownConc.map(v => { const n = parseMaybeNumber(v); return n === null ? NaN : n; });

        const payload = {
            assay: 1, concs, rccSignals: [refSig],
            igccConcs: igccPairs.map(p => p.dose),
            igccSignals: igccPairs.map(p => p.alu),
            unknownAlus, unknownDoses
        };

        try {
            const resp = await axios.post(RAWCURVE_POOLED_URL, payload, { headers: { "Content-Type": "application/json" }, timeout: 30000 });
            const data = resp.data;
            if (data?.status !== 1) {
                return { ok: false, reason: `backend status ${data?.status ?? "unknown"} - ${describeG06Status(data?.status, data?.message)}`, statusCode: data?.status ?? null };
            }
            // IGCC pairs were sent but the backend did not apply them � treat
            // as a failure so the status banner shows red, not a green checkmark.
            const pairsPresent = igccPairs.length > 0;
            if (pairsPresent && !data?.igcc_applied) {
                return { ok: false, reason: `Error 4: IGCC Curve Fit failed � the IGCC re-fit could not converge. Check that your IGCC calibrator ALU values are within the range of the RCC curve.`, statusCode: 1, errorCategory: "error4" };
            }
            return { ok: true, response: data, igccPairCount: igccPairs.length, igccPairsPresent: pairsPresent, mode };
        } catch (err) {
            return { ok: false, reason: err?.message || "request failed", statusCode: null };
        }
    }, [assays]);

    const applyResult = useCallback((assayIdx, mode, data, igccPairsPresent = false) => {
        const resultKey = mode === "igcc" ? "igccResult" : "rccResult";
        setAssays(prev => {
            const copy = [...prev];
            const a = { ...copy[assayIdx] };
            a[resultKey] = buildResult(a, data, rounding.significantDigits, rounding.precisionDigits, igccPairsPresent);
            copy[assayIdx] = a;
            return copy;
        });
    }, [rounding]);

    // Apply many results in ONE setState. updates = [{assayIdx, mode, data?, error?, statusCode?, igccPairsPresent?}]
    const applyResultsBatch = useCallback((updates) => {
        if (!updates.length) return;
        setAssays(prev => {
            const copy = [...prev];
            for (const u of updates) {
                const resultKey = u.mode === "igcc" ? "igccResult" : "rccResult";
                const a = { ...copy[u.assayIdx] };
                if (u.error) {
                    const errCat = u.errorCategory || classifyG06Error(u.statusCode ?? null, false, false);
                    a[resultKey] = {
                        ...a[resultKey],
                        status: "error",
                        statusCode: u.statusCode ?? null,
                        errorCategory: errCat === "ok" ? "error1" : errCat
                    };
                } else {
                    a[resultKey] = buildResult(a, u.data, rounding.significantDigits, rounding.precisionDigits, u.igccPairsPresent || false);
                }
                copy[u.assayIdx] = a;
            }
            return copy;
        });
    }, [rounding]);

    const markError = useCallback((assayIdx, mode, statusCode = null, errorCategory = null) => {
        const resultKey = mode === "igcc" ? "igccResult" : "rccResult";
        const errCat = errorCategory || classifyG06Error(statusCode, false, false);
        setAssays(prev => {
            const copy = [...prev];
            copy[assayIdx] = {
                ...copy[assayIdx],
                [resultKey]: {
                    ...copy[assayIdx][resultKey],
                    status: "error",
                    statusCode,
                    errorCategory: errCat === "ok" ? "error1" : errCat
                }
            };
            return copy;
        });
    }, []);

    // ---- Single assay handlers ----
    const handleCalculateActive = async (mode) => {
        const label = mode === "rcc" ? "RCC" : "IGCC";
        startOperation(`Calculating ${label} for ${assays[activeAssay].name}...`);
        const res = await runFitForAssay(activeAssay, mode);
        if (res.ok) {
            applyResult(activeAssay, mode, res.response, res.igccPairsPresent);
            const bent = res.response?.igcc_applied ? ` (IGCC bent, ${res.igccPairCount} pairs)` : (mode === "igcc" ? " (no valid IGCC pairs � plain RCC fit)" : "");
            finishOperation(`${assays[activeAssay].name} ${label} complete${bent}.`, "success");
        } else {
            markError(activeAssay, mode, res.statusCode ?? null, res.errorCategory ?? null);
            finishOperation(`${assays[activeAssay].name} ${label} failed: ${res.reason}.`, "error");
        }
    };

    // ---- Generate All ----
    const handleGenerateAll = async () => {
        const populated = assays.map((a, i) => i).filter(i => assays[i].rccAlu.some(v => parseMaybeNumber(v) !== null));
        if (populated.length === 0) { finishOperation("No populated assays to process.", "warning"); return; }

        let okRcc = 0, okIgcc = 0, errCount = 0;
        const total = populated.length * 2;
        let done = 0;
        const updates = [];

        for (const idx of populated) {
            startOperation(`Processing ${assays[idx].name} RCC (${++done}/${total})...`);
            const rccRes = await runFitForAssay(idx, "rcc");  // eslint-disable-line no-await-in-loop
            if (rccRes.ok) { updates.push({ assayIdx: idx, mode: "rcc", data: rccRes.response, igccPairsPresent: false }); okRcc++; } else { updates.push({ assayIdx: idx, mode: "rcc", error: true, statusCode: rccRes.statusCode, errorCategory: rccRes.errorCategory ?? null }); errCount++; }

            // Only run IGCC if this assay has Master ALU data. Never fall back
            // to running a plain RCC fit in place of a failing IGCC.
            const hasIgccData = assays[idx].masterAlu.some(v => parseMaybeNumber(v) !== null);
            if (!hasIgccData) {
                done++;
                continue;
            }
            startOperation(`Processing ${assays[idx].name} IGCC (${++done}/${total})...`);
            const igccRes = await runFitForAssay(idx, "igcc"); // eslint-disable-line no-await-in-loop
            if (igccRes.ok) { updates.push({ assayIdx: idx, mode: "igcc", data: igccRes.response, igccPairsPresent: igccRes.igccPairsPresent }); okIgcc++; } else { updates.push({ assayIdx: idx, mode: "igcc", error: true, statusCode: igccRes.statusCode, errorCategory: igccRes.errorCategory ?? null }); errCount++; }
        }

        // Single state update for the whole batch - prevents the render storm.
        applyResultsBatch(updates);
        finishOperation(
            `Generate All complete: ${okRcc} RCC, ${okIgcc} IGCC (${errCount} failed).`,
            errCount > 0 ? "warning" : "success"
        );
    };

    // ---- Process all per-mode ----
    const handleProcessAllAssays = async (mode) => {
        const label = mode === "rcc" ? "RCC" : "IGCC";
        // For RCC mode: include any assay with rccAlu data.
        // For IGCC mode: only include assays that have masterAlu data �
        // never fall back to rccAlu when Calculate IGCC All is pressed.
        const populated = assays.map((a, i) => i).filter(i => {
            if (mode === "rcc") return assays[i].rccAlu.some(v => parseMaybeNumber(v) !== null);
            return assays[i].masterAlu.some(v => parseMaybeNumber(v) !== null);
        });
        if (populated.length === 0) { finishOperation(`No assays with ${label} data to process.`, "warning"); return; }

        let okCount = 0, errCount = 0, bentCount = 0;
        const updates = [];
        for (let k = 0; k < populated.length; k++) {
            const idx = populated[k];
            startOperation(`Calculating ${label} for ${assays[idx].name} (${k + 1}/${populated.length})...`);
            const res = await runFitForAssay(idx, mode); // eslint-disable-line no-await-in-loop
            if (res.ok) {
                updates.push({ assayIdx: idx, mode, data: res.response, igccPairsPresent: res.igccPairsPresent });
                okCount++;
                if (res.response?.igcc_applied) bentCount++;
            } else {
                updates.push({ assayIdx: idx, mode, error: true, statusCode: res.statusCode, errorCategory: res.errorCategory ?? null });
                errCount++;
            }
        }
        applyResultsBatch(updates);
        finishOperation(`${label}: ${okCount}/${populated.length} ok (${bentCount} IGCC bent, ${errCount} failed).`, errCount > 0 ? "warning" : "success");
    };

    // ---- Chart helpers ----
    const escapeXml = s => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");

    const buildChartSvgString = ({ linePts = [], rawPts = [], igccPts = [], title = chartTitle } = {}) => {
        const all = [...linePts, ...rawPts, ...igccPts];
        if (all.length < 2) return null;
        const W = 874, H = 540, leftPad = 70, rightPad = 20, topPad = 60, bottomPad = 50;
        const cw = W - leftPad - rightPad, ch = H - topPad - bottomPad;
        const xs = all.map(p => p.x), ys = all.map(p => p.y);
        const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
        const sx = x => leftPad + ((x - minX) / ((maxX - minX) || 1)) * cw;
        const sy = y => topPad + ch - ((y - minY) / ((maxY - minY) || 1)) * ch;
        const xTicks = Array.from({ length: 5 }, (_, i) => { const v = minX + ((maxX - minX) * i) / 4; return { v, x: sx(v) }; });
        const yTicks = Array.from({ length: 5 }, (_, i) => { const v = minY + ((maxY - minY) * (4 - i)) / 4; return { v, y: sy(v) }; });
        const parts = [];
        parts.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><rect width="${W}" height="${H}" fill="#fafafa"/>`);
        parts.push(`<text x="${leftPad + cw / 2}" y="42" text-anchor="middle" font-size="14" font-weight="bold" fill="#333">${chartTitle}</text>`);
        parts.push(`<line x1="${leftPad}" y1="${topPad + ch}" x2="${leftPad + cw}" y2="${topPad + ch}" stroke="#555"/><line x1="${leftPad}" y1="${topPad}" x2="${leftPad}" y2="${topPad + ch}" stroke="#555"/>`);
        for (const t of xTicks) {
            parts.push(`<line x1="${t.x}" y1="${topPad + ch}" x2="${t.x}" y2="${topPad + ch + 5}" stroke="#555"/><text x="${t.x}" y="${topPad + ch + 17}" text-anchor="middle" font-size="10" fill="#333">${Number(t.v).toFixed(2)}</text>`);
        }
        for (const t of yTicks) {
            parts.push(`<line x1="${leftPad - 5}" y1="${t.y}" x2="${leftPad}" y2="${t.y}" stroke="#555"/><line x1="${leftPad}" y1="${t.y}" x2="${leftPad + cw}" y2="${t.y}" stroke="#e0e0e0"/><text x="${leftPad - 8}" y="${t.y + 3}" text-anchor="end" font-size="10" fill="#333">${Number(t.v).toFixed(2)}</text>`);
        }
        parts.push(`<text x="${leftPad + cw / 2}" y="${topPad + ch + 40}" text-anchor="middle" font-size="12" font-weight="bold" fill="#333">${xAxisLabel}</text>`);
        parts.push(`<text x="12" y="${topPad + ch / 2}" text-anchor="middle" font-size="12" font-weight="bold" fill="#333" transform="rotate(-90 12 ${topPad + ch / 2})">${yAxisLabel}</text>`);
        if (linePts.length > 0) {
            parts.push(`<polyline fill="none" stroke="#1976d2" stroke-width="2" points="${linePts.map(p => `${sx(p.x)},${sy(p.y)}`).join(" ")}"/>`);
            for (const p of linePts) parts.push(`<circle cx="${sx(p.x)}" cy="${sy(p.y)}" r="3" fill="#1976d2" stroke="white" stroke-width="1.5"/>`);
        }
        for (const p of rawPts) parts.push(`<circle cx="${sx(p.x)}" cy="${sy(p.y)}" r="4" fill="#d32f2f"/>`);
        for (const p of igccPts) {
            const cx = sx(p.x), cy = sy(p.y), arm = 6;
            parts.push(`<line x1="${cx - arm}" y1="${cy}" x2="${cx + arm}" y2="${cy}" stroke="#7b1fa2" stroke-width="2.25" stroke-linecap="round"/><line x1="${cx}" y1="${cy - arm}" x2="${cx}" y2="${cy + arm}" stroke="#7b1fa2" stroke-width="2.25" stroke-linecap="round"/>`);
        }
        parts.push("</svg>");
        return parts.join("");
    };

    const rasterizeSvgToPng = svgStr => new Promise((resolve, reject) => {
        const dataUri = "data:image/svg+xml;charset=utf-8;base64," + btoa(unescape(encodeURIComponent(svgStr)));
        const img = new Image();
        img.onload = () => {
            try {
                const canvas = document.createElement("canvas");
                canvas.width = 874; canvas.height = 540;
                const ctx = canvas.getContext("2d");
                ctx.fillStyle = "#fafafa"; ctx.fillRect(0, 0, 874, 540);
                ctx.drawImage(img, 0, 0, 874, 540);
                canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("canvas.toBlob null")), "image/png");
            } catch (e) { reject(e); }
        };
        img.onerror = e => reject(e);
        img.src = dataUri;
    });

    const blobToArrayBuffer = blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
    });

    // ---- Excel export ----
    const handleExportToExcel = async () => {
        try {
            startOperation("Building Excel export...");
            const exportedAt = new Date();
            const stampForFile = exportedAt.toISOString().replace(/[:.]/g, "-").slice(0, 19);
            const wb = new ExcelJS.Workbook();
            wb.creator = "G06 Multi-Assay";
            wb.created = exportedAt;
            const wsSum = wb.addWorksheet("Summary");

            // Filter: Only include assays that have been run (have RCC or IGCC results)
            const runAssays = assays.filter(a => a.rccResult.status !== "" || a.igccResult.status !== "");
            if (runAssays.length === 0) {
                finishOperation("No assays have been run yet. Run at least one calculation to export.", "warning");
                return;
            }

            // Timestamp
            wsSum.getCell(1, 1).value = "Exported:"; wsSum.getCell(1, 1).font = { bold: true };
            wsSum.getCell(1, 2).value = exportedAt.toLocaleString();

            // Helper: write a horizontal section block (label col + one col per assay)
            // title      -- bold title row spanning all columns
            // labelRows  -- array of { label, values[] } where values[i] = value for assay i
            // Returns the next available row number
            const writeSumSection = (ws, startRow, title, labelRows) => {
                let r = startRow;
                // Section title
                ws.getCell(r, 1).value = title;
                ws.getCell(r, 1).font = { bold: true };
                r++;
                // Data rows
                for (const { label, values } of labelRows) {
                    ws.getCell(r, 1).value = label;
                    values.forEach((v, ai) => { ws.getCell(r, 2 + ai).value = v; });
                    r++;
                }
                return r + 1;
            };

            // Assay name header row
            const headerRow = 3;
            wsSum.getCell(headerRow, 1).value = "Assay Inputs";
            wsSum.getCell(headerRow, 1).font = { bold: true, size: 12 };
            // Assay name headers in row 4 (only for run assays)
            wsSum.getCell(4, 1).value = "";
            runAssays.forEach((a, ai) => {
                wsSum.getCell(4, 2 + ai).value = a.name;
                wsSum.getCell(4, 2 + ai).font = { bold: true };
            });

            let sumRow = 5;

            // ---- Input sections (same order as page) - only for run assays ----
            sumRow = writeSumSection(wsSum, sumRow, "Ref Cal Conc",
                Array.from({ length: REF_CAL_LEVELS }, (_, i) => ({
                    label: `Level ${i + 1}`,
                    values: runAssays.map(a => a.refCalConc[i] ?? "")
                }))
            );
            sumRow = writeSumSection(wsSum, sumRow, "Master Curve ALU (MGCC)",
                Array.from({ length: MASTER_ALU_LEVELS }, (_, i) => ({
                    label: `Level ${i + 1}`,
                    values: runAssays.map(a => a.masterAlu[i] ?? "")
                }))
            );
            sumRow = writeSumSection(wsSum, sumRow, "Reference Calibrator ALU (RCC)",
                Array.from({ length: RCC_ALU_LEVELS }, (_, i) => ({
                    label: `Level ${i + 1}`,
                    values: runAssays.map(a => a.rccAlu[i] ?? "")
                }))
            );
            sumRow = writeSumSection(wsSum, sumRow, "IGCC Calibrator ALU",
                Array.from({ length: IGCC_LEVELS }, (_, i) => ({
                    label: `Cal ${i + 1}`,
                    values: runAssays.map(a => a.igccAlu[i] ?? "")
                }))
            );
            sumRow = writeSumSection(wsSum, sumRow, "IGCC Calibrator Conc",
                Array.from({ length: IGCC_LEVELS }, (_, i) => ({
                    label: `Cal ${i + 1}`,
                    values: runAssays.map(a => a.igccConc[i] ?? "")
                }))
            );
            sumRow = writeSumSection(wsSum, sumRow, "Unknown ALU Inputs",
                Array.from({ length: unknownRowCount }, (_, i) => ({
                    label: `#${i + 1}`,
                    values: runAssays.map(a => a.unknownAlu[i] ?? "")
                }))
            );
            sumRow = writeSumSection(wsSum, sumRow, "Unknown Conc Inputs",
                Array.from({ length: unknownRowCount }, (_, i) => ({
                    label: `#${i + 1}`,
                    values: runAssays.map(a => a.unknownConc[i] ?? "")
                }))
            );

            // ---- Output sections ----
            wsSum.getCell(sumRow, 1).value = "Outputs";
            wsSum.getCell(sumRow, 1).font = { bold: true, size: 12 };
            // Re-write assay name headers at the output section too (only for run assays)
            runAssays.forEach((a, ai) => {
                wsSum.getCell(sumRow, 2 + ai).value = a.name;
                wsSum.getCell(sumRow, 2 + ai).font = { bold: true };
            });
            sumRow++;

            // RCC outputs
            sumRow = writeSumSection(wsSum, sumRow, "RCC Results", [
                { label: "Status",       values: runAssays.map(a => a.rccResult.status || "(not run)") },
                { label: "Return Code",  values: runAssays.map(a => a.rccResult.statusCode != null ? `[${a.rccResult.statusCode}]` : "") },
                { label: "Fit Factor",   values: runAssays.map(a => a.rccResult.fitFactor) },
                { label: "Shape Factor", values: runAssays.map(a => a.rccResult.shapeFactor) },
                { label: "a",            values: runAssays.map(a => a.rccResult.params.a) },
                { label: "b",            values: runAssays.map(a => a.rccResult.params.b) },
                { label: "c",            values: runAssays.map(a => a.rccResult.params.c) },
                { label: "d",            values: runAssays.map(a => a.rccResult.params.d) },
                { label: "n",            values: runAssays.map(a => a.rccResult.params.n) },
                ...Array.from({ length: unknownRowCount }, (_, i) => ({
                    label: `RCC Fitted Dose #${i + 1}`,
                    values: runAssays.map(a => a.rccResult.fittedDoseFromAlu[i] ?? "")
                })),
                ...Array.from({ length: unknownRowCount }, (_, i) => ({
                    label: `RCC Fitted ALU #${i + 1}`,
                    values: runAssays.map(a => a.rccResult.fittedAluFromDose[i] ?? "")
                }))
            ]);

            // IGCC outputs
            sumRow = writeSumSection(wsSum, sumRow, "IGCC Results", [
                { label: "Status",        values: runAssays.map(a => a.igccResult.status || "(not run)") },
                { label: "Return Code",   values: runAssays.map(a => a.igccResult.statusCode != null ? `[${a.igccResult.statusCode}]` : "") },
                { label: "IGCC Applied",  values: runAssays.map(a => a.igccResult.igccApplied ? "Yes" : (a.igccResult.status === "ok" ? "No" : "")) },
                { label: "Fit Factor",    values: runAssays.map(a => a.igccResult.fitFactor) },
                { label: "Shape Factor",  values: runAssays.map(a => a.igccResult.shapeFactor) },
                { label: "a",             values: runAssays.map(a => a.igccResult.params.a) },
                { label: "b",             values: runAssays.map(a => a.igccResult.params.b) },
                { label: "c",             values: runAssays.map(a => a.igccResult.params.c) },
                { label: "d",             values: runAssays.map(a => a.igccResult.params.d) },
                { label: "n",             values: runAssays.map(a => a.igccResult.params.n) },
                ...Array.from({ length: unknownRowCount }, (_, i) => ({
                    label: `IGCC Fitted Dose #${i + 1}`,
                    values: runAssays.map(a => a.igccResult.fittedDoseFromAlu[i] ?? "")
                })),
                ...Array.from({ length: unknownRowCount }, (_, i) => ({
                    label: `IGCC Fitted ALU #${i + 1}`,
                    values: runAssays.map(a => a.igccResult.fittedAluFromDose[i] ?? "")
                }))
            ]);

            // Column widths: label col wide, assay cols uniform
            wsSum.getColumn(1).width = 24;
            for (let c = 2; c < 2 + runAssays.length; c++) wsSum.getColumn(c).width = 14;

            // ---- Per-assay sheets - only for run assays ----
            for (let idx = 0; idx < runAssays.length; idx++) {
                const a = runAssays[idx];
                const ws = wb.addWorksheet(a.name.slice(0, 31));

                const writeBlock = (startRow, startCol, title, headers, rows) => {
                    let r = startRow;
                    if (title) { const c = ws.getCell(r, startCol); c.value = title; c.font = { bold: true }; r++; }
                    if (headers) {
                        headers.forEach((h, i) => { const c = ws.getCell(r, startCol + i); c.value = h; c.font = { bold: true }; });
                        r++;
                    }
                    for (const row of rows) { row.forEach((v, i) => { ws.getCell(r, startCol + i).value = v; }); r++; }
                    return r + 1;
                };

                // Column 1: Inputs
                let r = 1;
                r = writeBlock(r, 1, "Ref Cal Conc", ["Level", "Value"], Array.from({ length: REF_CAL_LEVELS }, (_, i) => [i + 1, a.refCalConc[i]]));
                r = writeBlock(r, 1, "Master Curve ALU", ["Level", "ALU"], Array.from({ length: MASTER_ALU_LEVELS }, (_, i) => [i + 1, a.masterAlu[i]]));
                r = writeBlock(r, 1, "Reference Calibrator ALU (RCC)", ["Level", "ALU"], Array.from({ length: RCC_ALU_LEVELS }, (_, i) => [i + 1, a.rccAlu[i]]));
                r = writeBlock(r, 1, "IGCC Calibrators", ["#", "ALU", "Derived Conc"], Array.from({ length: IGCC_LEVELS }, (_, i) => [i + 1, a.igccAlu[i], a.igccConc[i]]));
                r = writeBlock(r, 1, "Unknown ALU Inputs", ["#", "Input ALU"], Array.from({ length: unknownRowCount }, (_, i) => [i + 1, a.unknownAlu[i]]));
                writeBlock(r, 1, "Unknown Conc Inputs", ["#", "Input Conc"], Array.from({ length: unknownRowCount }, (_, i) => [i + 1, a.unknownConc[i]]));

                // Column 6: RCC outputs
                let rr = 1;
                ws.getCell(rr, 6).value = "RCC Results"; ws.getCell(rr, 6).font = { bold: true }; rr++;
                ws.getCell(rr, 6).value = `Status: ${a.rccResult.status || "(not run)"}`;  rr++;
                [["Fit Factor", a.rccResult.fitFactor], ["Shape Factor", a.rccResult.shapeFactor]].forEach(([k, v]) => { ws.getCell(rr, 6).value = k; ws.getCell(rr, 7).value = v; rr++; });
                [["a", a.rccResult.params.a], ["b", a.rccResult.params.b], ["c", a.rccResult.params.c], ["d", a.rccResult.params.d], ["n", a.rccResult.params.n]].forEach(([k, v]) => { ws.getCell(rr, 6).value = k; ws.getCell(rr, 7).value = v; rr++; });
                rr++;
                rr = writeBlock(rr, 6, "RCC Unknown ALU -> Fitted Dose", ["#", "Input ALU", "Fitted Dose"], Array.from({ length: unknownRowCount }, (_, i) => [i + 1, a.unknownAlu[i], a.rccResult.fittedDoseFromAlu[i]]));
                rr = writeBlock(rr, 6, "RCC Unknown Conc -> Fitted ALU", ["#", "Input Conc", "Fitted ALU"], Array.from({ length: unknownRowCount }, (_, i) => [i + 1, a.unknownConc[i], a.rccResult.fittedAluFromDose[i]]));

                // Column 11: IGCC outputs
                let ri = 1;
                ws.getCell(ri, 11).value = "IGCC Results"; ws.getCell(ri, 11).font = { bold: true }; ri++;
                ws.getCell(ri, 11).value = `Status: ${a.igccResult.status || "(not run)"}  IGCC Applied: ${a.igccResult.igccApplied ? "Yes" : "No"}`; ri++;
                [["Fit Factor", a.igccResult.fitFactor], ["Shape Factor", a.igccResult.shapeFactor]].forEach(([k, v]) => { ws.getCell(ri, 11).value = k; ws.getCell(ri, 12).value = v; ri++; });
                [["a", a.igccResult.params.a], ["b", a.igccResult.params.b], ["c", a.igccResult.params.c], ["d", a.igccResult.params.d], ["n", a.igccResult.params.n]].forEach(([k, v]) => { ws.getCell(ri, 11).value = k; ws.getCell(ri, 12).value = v; ri++; });
                ri++;
                ri = writeBlock(ri, 11, "IGCC Unknown ALU -> Fitted Dose", ["#", "Input ALU", "Fitted Dose"], Array.from({ length: unknownRowCount }, (_, i) => [i + 1, a.unknownAlu[i], a.igccResult.fittedDoseFromAlu[i]]));
                ri = writeBlock(ri, 11, "IGCC Unknown Conc -> Fitted ALU", ["#", "Input Conc", "Fitted ALU"], Array.from({ length: unknownRowCount }, (_, i) => [i + 1, a.unknownConc[i], a.igccResult.fittedAluFromDose[i]]));

                const writeCurvePointsNextToChart = (startCol, startRow, title, points) => {
                    ws.getCell(startRow, startCol).value = title;
                    ws.getCell(startRow, startCol).font = { bold: true };
                    ws.getCell(startRow + 1, startCol).value = "#";
                    ws.getCell(startRow + 1, startCol + 1).value = "Dose";
                    ws.getCell(startRow + 1, startCol + 2).value = "Signal";
                    ws.getCell(startRow + 1, startCol).font = { bold: true };
                    ws.getCell(startRow + 1, startCol + 1).font = { bold: true };
                    ws.getCell(startRow + 1, startCol + 2).font = { bold: true };

                    points.forEach((p, i) => {
                        ws.getCell(startRow + 2 + i, startCol).value = i + 1;
                        ws.getCell(startRow + 2 + i, startCol + 1).value = Number(p.x).toFixed(4);
                        ws.getCell(startRow + 2 + i, startCol + 2).value = Number(p.y).toFixed(4);
                    });
                };

                // Embed RCC chart at col 16 and put curve-point lists side-by-side to the right
                // Both RCC and IGCC curve tables start on the same row (CURVE_TABLE_ROW)
                const RCC_CHART_COL = 16;
                const RCC_CHART_ROW = 0;
                const CURVE_TABLE_ROW = 1;
                const RCC_CURVE_TABLE_COL = 29;
                const IGCC_CURVE_TABLE_COL = 33; // One column gap (32) between them

                if (a.rccResult.curvePoints.length > 0) {
                    writeCurvePointsNextToChart(RCC_CURVE_TABLE_COL, CURVE_TABLE_ROW, "RCC Curve Points", a.rccResult.curvePoints);
                }
                const rccRawPts = [];
                for (let i = 0; i < REF_CAL_LEVELS; i++) {
                    const x = parseMaybeNumber(a.refCalConc[i]), y = parseMaybeNumber(a.rccAlu[i]);
                    if (x !== null && y !== null) rccRawPts.push({ x, y });
                }
                if (a.rccResult.curvePoints.length >= 2 && rccRawPts.length >= 2) {
                    try {
                        const svg = buildChartSvgString({ linePts: a.rccResult.curvePoints, rawPts: rccRawPts, igccPts: [], title: chartTitle });
                        if (svg) {
                            const blob = await rasterizeSvgToPng(svg);
                            const buf = await blobToArrayBuffer(blob);
                            const imgId = wb.addImage({ buffer: buf, extension: "png" });
                            ws.addImage(imgId, { tl: { col: RCC_CHART_COL, row: RCC_CHART_ROW }, br: { col: 28, row: 25 } });
                        }
                    } catch (e) { console.warn("RCC chart embed failed:", e); }
                }

                // Embed IGCC chart below RCC chart, and put its curve-point list next to RCC table (same start row)
                const IGCC_CHART_COL = 16;
                const IGCC_CHART_ROW = 27;
                if (a.igccResult.curvePoints.length > 0) {
                    writeCurvePointsNextToChart(IGCC_CURVE_TABLE_COL, CURVE_TABLE_ROW, "IGCC Curve Points", a.igccResult.curvePoints);
                }
                const igccRawPts = [];
                for (let i = 0; i < MASTER_ALU_LEVELS; i++) {
                    const x = parseMaybeNumber(a.refCalConc[i]), y = parseMaybeNumber(a.masterAlu[i]);
                    if (x !== null && y !== null) igccRawPts.push({ x, y });
                }
                const igccPseudoPts = [];
                if (a.igccResult.igccApplied) {
                    for (let i = 0; i < IGCC_LEVELS; i++) {
                        const x = parseMaybeNumber(a.igccConc[i]), y = parseMaybeNumber(a.igccAlu[i]);
                        if (x !== null && y !== null) igccPseudoPts.push({ x, y });
                    }
                }
                if (a.igccResult.curvePoints.length >= 2 && igccRawPts.length >= 2) {
                    try {
                        const svg = buildChartSvgString({ linePts: a.igccResult.curvePoints, rawPts: igccRawPts, igccPts: igccPseudoPts, title: chartTitle });
                        if (svg) {
                            const blob = await rasterizeSvgToPng(svg);
                            const buf = await blobToArrayBuffer(blob);
                            const imgId = wb.addImage({ buffer: buf, extension: "png" });
                            ws.addImage(imgId, { tl: { col: IGCC_CHART_COL, row: IGCC_CHART_ROW }, br: { col: 28, row: 52 } });
                        }
                    } catch (e) { console.warn("IGCC chart embed failed:", e); }
                }

                for (let c = 1; c <= 5; c++) ws.getColumn(c).width = 14;
                for (let c = 6; c <= 15; c++) ws.getColumn(c).width = 14;
                for (let c = 16; c <= 28; c++) ws.getColumn(c).width = 12;
                for (let c = 29; c <= 31; c++) ws.getColumn(c).width = 12; // RCC table
                for (let c = 33; c <= 35; c++) ws.getColumn(c).width = 12; // IGCC table
            }

            const buf = await wb.xlsx.writeBuffer();
            saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `G06_MultiAssay_${stampForFile}.xlsx`);
            finishOperation(`Exported: 1 summary + ${runAssays.length} assay sheet${runAssays.length === 1 ? "" : "s"} with RCC + IGCC data and charts.`, "success");
        } catch (err) {
            console.error(err);
            finishOperation(`Export failed: ${err?.message || err}`, "error");
        }
    };

    // ---- Import ----
    const handleImportFromExcel = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        startOperation(`Importing ${file.name}...`);
        try {
            const buf = await file.arrayBuffer();
            const wb = XLSX.read(new Uint8Array(buf), { type: "array" });
            const sheetName = wb.SheetNames.find(n => /multi/i.test(n)) || wb.SheetNames[0];
            const ws = wb.Sheets[sheetName];
            const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            const labelCol = 5, firstAssayCol = 7;
            const findRow = needle => rows.findIndex(r => String(r[labelCol] ?? "").toLowerCase().includes(needle));

            const refRow = findRow("ref cal conc");
            const masterRow = findRow("master curve alu");
            const rccRow = findRow("reference calibrator alu");
            const calAluRow = findRow("calibrator alu");
            const calDoseRow = findRow("calibrator derived");
            const unkAluRow = findRow("unkown alu") >= 0 ? findRow("unkown alu") : findRow("unknown alu");
            const unkConcRow = findRow("unknown conc");

            if (refRow < 0) { finishOperation("Import failed: could not find 'Ref Cal Conc' label.", "error"); return; }

            const headerRow = rows[4] || [];
            let numAssays = 0;
            for (let c = firstAssayCol; c < headerRow.length; c++) {
                if (String(headerRow[c] ?? "").toLowerCase().includes("assay")) numAssays++;
                else break;
            }
            numAssays = Math.max(1, Math.min(200, numAssays || DEFAULT_ASSAY_COUNT));

            const newRefCal = Array(REF_CAL_LEVELS).fill("");
            for (let i = 0; i < REF_CAL_LEVELS; i++) {
                const rw = rows[refRow + i];
                if (rw) { const v = parseMaybeNumber(rw[firstAssayCol - 1]); newRefCal[i] = v !== null ? String(v) : (rw[firstAssayCol - 1] ?? ""); }
            }

            const readBlock = (startRow, count, ai) => {
                const out = Array(count).fill("");
                for (let i = 0; i < count; i++) {
                    const rw = rows[startRow + i];
                    if (!rw) continue;
                    const v = rw[firstAssayCol + ai];
                    if (v !== "" && v !== null && v !== undefined) out[i] = String(v);
                }
                return out;
            };

            const unkRows = Math.max(DEFAULT_UNKNOWN_ROWS, unknownRowCount);
            const newAssays = Array.from({ length: numAssays }, (_, ai) => {
                const a = makeEmptyAssay(ai, unkRows);
                a.name = String(headerRow[firstAssayCol + ai] || a.name);
                a.refCalConc = [...newRefCal];
                if (masterRow >= 0) a.masterAlu = readBlock(masterRow, MASTER_ALU_LEVELS, ai);
                if (rccRow >= 0) a.rccAlu = readBlock(rccRow, RCC_ALU_LEVELS, ai);
                if (calAluRow >= 0) a.igccAlu = readBlock(calAluRow, IGCC_LEVELS, ai);
                if (calDoseRow >= 0) a.igccConc = readBlock(calDoseRow, IGCC_LEVELS, ai);
                if (unkAluRow >= 0) a.unknownAlu = readBlock(unkAluRow, unkRows, ai);
                if (unkConcRow >= 0) a.unknownConc = readBlock(unkConcRow, unkRows, ai);
                return a;
            });

            setAssays(newAssays);
            setActiveAssay(0);
            finishOperation(`Imported ${numAssays} assay${numAssays === 1 ? "" : "s"} from ${sheetName}.`, "success");
        } catch (err) {
            console.error(err);
            finishOperation(`Import failed: ${err?.message || err}`, "error");
        }
    };

    // ---- Chart data for active assay ----
    // activeAssayData drives the Calculate button labels and the active-column highlight.
    // chartAssayData drives the chart preview -- independently selectable via the
    // assay selector strip at the top of the chart section.
    const activeAssayData = assays[activeAssay];
    const chartAssayData = assays[chartAssay];
    const activeCurvePts = chartAssayData?.rccResult.curvePoints || [];
    const activeIgccCurvePts = chartAssayData?.igccResult.curvePoints || [];
    const activeRawPts = useMemo(() => {
        if (!chartAssayData) return [];
        const out = [];
        for (let i = 0; i < REF_CAL_LEVELS; i++) {
            const x = parseMaybeNumber(chartAssayData.refCalConc[i]);
            const y = parseMaybeNumber(chartAssayData.rccAlu[i]);
            if (x !== null && y !== null) out.push({ x, y });
        }
        return out;
    }, [chartAssayData]);
    const activeIgccCalPts = useMemo(() => {
        if (!chartAssayData || !chartAssayData.igccResult.igccApplied) return [];
        const out = [];
        for (let i = 0; i < IGCC_LEVELS; i++) {
            const x = parseMaybeNumber(chartAssayData.igccConc[i]);
            const y = parseMaybeNumber(chartAssayData.igccAlu[i]);
            if (x !== null && y !== null) out.push({ x, y });
        }
        return out;
    }, [chartAssayData]);

    // ---- Readonly cell grid + output panel (memoized components defined above) ----
    const activePanelAssay = activeAssay;

    // ---- Render ----
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>G06 Multi-Assay</Typography>
            <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
                Process many assays side-by-side. Each column is an independent G06 run with its own RCC + IGCC results stored separately.
            </Typography>

            {/* Action bar */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
                <Button variant="contained" color="primary" onClick={() => handleCalculateActive("rcc")} disabled={operationStatus.busy}>
                    Calculate RCC ({activeAssayData?.name || "Active"})
                </Button>
                <Button variant="contained" color="secondary" onClick={() => handleCalculateActive("igcc")} disabled={operationStatus.busy}>
                    Calculate IGCC ({activeAssayData?.name || "Active"})
                </Button>
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <Button variant="outlined" color="primary" onClick={() => handleProcessAllAssays("rcc")} disabled={operationStatus.busy}>
                    Calculate RCC (All)
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => handleProcessAllAssays("igcc")} disabled={operationStatus.busy}>
                    Calculate IGCC (All)
                </Button>
                <Button variant="contained" color="success" onClick={handleGenerateAll} disabled={operationStatus.busy}
                    title="Run both RCC and IGCC on every populated assay column sequentially.">
                    Generate All (RCC + IGCC)
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" color="warning" onClick={handleClearAll}>Clear All</Button>
                <Button variant="outlined" onClick={handleExportToExcel} disabled={operationStatus.busy}>Export to Excel</Button>
            </Box>

            {/* Assay count controls */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 2 }}>
                <Typography variant="caption" sx={{ color: "text.secondary", mr: 1 }}>Assays:</Typography>
                <Button size="small" variant="outlined" onClick={() => removeAssays(10)} disabled={assays.length <= 1}>-10</Button>
                <Button size="small" variant="outlined" onClick={() => removeAssays(1)} disabled={assays.length <= 1}>-1</Button>
                <TextField
                    type="number"
                    size="small"
                    value={assayCountDraft}
                    onChange={e => setAssayCountDirect(Number(e.target.value) || 1)}
                    inputProps={{ min: 1, max: 200, style: { width: 52, textAlign: "center" } }}
                    sx={{ width: 80 }}
                />
                <Button size="small" variant="contained" color="primary" sx={{ fontSize: 11, py: 0.5 }} onClick={confirmAssayCount}>Apply</Button>
                <Button size="small" variant="outlined" onClick={() => addAssays(1)}>+1</Button>
                <Button size="small" variant="outlined" onClick={() => addAssays(10)}>+10</Button>
            </Box>

            {/* Status banner */}
            <Alert severity={operationStatus.level === "error" ? "error" : operationStatus.level === "warning" ? "warning" : operationStatus.level === "success" ? "success" : "info"}
                icon={operationStatus.busy ? <CircularProgress size={16} />
                    : operationStatus.level === "error" ? <ErrorOutlineIcon fontSize="small" />
                    : operationStatus.level === "warning" ? <WarningAmberIcon fontSize="small" />
                    : operationStatus.level === "success" ? <CheckCircleOutlineIcon fontSize="small" />
                    : undefined}
                sx={{ mb: 2 }}>
                {operationStatus.busy ? "Working... " : ""}{operationStatus.message}
                {operationStatus.at && <Box component="span" sx={{ ml: 1.5, opacity: 0.7, fontFamily: "monospace", fontSize: "0.85em" }}>[{operationStatus.at.toLocaleString()}]</Box>}
            </Alert>

            {/* Rounding */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField label="Sig Digits" type="number" size="small" value={rounding.significantDigits} onChange={e => setRounding({ ...rounding, significantDigits: Number(e.target.value) })} />
                <TextField label="Precision" type="number" size="small" value={rounding.precisionDigits} onChange={e => setRounding({ ...rounding, precisionDigits: Number(e.target.value) })} />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Active assay tab strip or dropdown */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1, px: 0.5, py: 0.5, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="caption" sx={{ alignSelf: "center", mr: 1, color: "text.secondary", whiteSpace: "nowrap" }}>Active Assay:</Typography>

                {assays.length < 25 ? (
                    // Button strip for <25 assays
                    assays.map((a, i) => {
                        const rccOk = a.rccResult.status === "ok", rccErr = a.rccResult.status === "error";
                        const igccOk = a.igccResult.status === "ok", igccErr = a.igccResult.status === "error";
                        const anyErr = rccErr || igccErr;
                        const allOk = rccOk && igccOk;
                        return (
                            <Button key={i} size="small" variant={i === activeAssay ? "contained" : "outlined"}
                                onClick={() => setActiveAssay(i)}
                                sx={{ minWidth: 60, height: 26, p: 0, fontSize: 11, lineHeight: 1, textTransform: "none", borderColor: allOk ? "success.main" : anyErr ? "error.main" : undefined }}
                                title={`RCC: ${a.rccResult.status || "not run"} / IGCC: ${a.igccResult.status || "not run"}`}>
                                {a.name}
                            </Button>
                        );
                    })
                ) : (
                    // Dropdown for >=25 assays
                    <Box sx={{ position: "relative" }}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={() => setActiveAssaySearchOpen(!activeAssaySearchOpen)}
                            sx={{ minWidth: 200, height: 26, p: 0.5, fontSize: 11 }}
                        >
                            {`Selected: ${assays[activeAssay]?.name || "Select"}`} ?
                        </Button>
                        {activeAssaySearchOpen && (
                            <Paper sx={{ position: "absolute", top: "100%", left: 0, zIndex: 1000, mt: 0.5, minWidth: 200, maxHeight: 400, overflowY: "auto" }}>
                                <TextField
                                    autoFocus
                                    size="small"
                                    placeholder="Search by name or #..."
                                    value={activeAssaySearch}
                                    onChange={e => setActiveAssaySearch(e.target.value)}
                                    sx={{ width: "100%", p: 1, "& .MuiOutlinedInput-root": { fontSize: 12 } }}
                                    onKeyDown={e => {
                                        if (e.key === "Escape") setActiveAssaySearchOpen(false);
                                    }}
                                />
                                {filteredActiveAssays.map((item) => (
                                    <Button
                                        key={item.idx}
                                        fullWidth
                                        variant={item.idx === activeAssay ? "contained" : "text"}
                                        onClick={() => { setActiveAssay(item.idx); setActiveAssaySearchOpen(false); setActiveAssaySearch(""); }}
                                        sx={{ justifyContent: "flex-start", fontSize: 12, px: 1, py: 0.5 }}
                                    >
                                        #{item.idx + 1}: {item.name}
                                    </Button>
                                ))}
                            </Paper>
                        )}
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Inputs */}
            <Button onClick={() => setInputsOpen(!inputsOpen)} startIcon={inputsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mb: 1 }}>Assay Inputs</Button>
            <Collapse in={inputsOpen} unmountOnExit>
                <Paper ref={containerRef} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ overflowX: "auto", width: "100%", mb: 1 }}>
                        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", width: "max-content" }}>
                            <Box sx={{ width: LABEL_COL_WIDTH, fontSize: 11, fontWeight: 600, color: "text.secondary", flexShrink: 0 }}>Assay</Box>
                            {assays.map((a, ai) => (
                                <TextField key={ai} size="small" value={a.name} onChange={e => setAssayName(ai, e.target.value)}
                                    sx={{ width: colWidth, flexShrink: 0, "& .MuiInputBase-input": { py: 0.25, fontSize: 11, textAlign: "center", fontWeight: 600 }, "& .MuiOutlinedInput-root": { height: 24, backgroundColor: ai === activeAssay ? "rgba(25,118,210,0.08)" : undefined } }} />
                            ))}
                        </Box>
                    </Box>

                  ={[
                        { key: "refCalConc",  label: "Ref Cal Conc (per assay)",          levels: REF_CAL_LEVELS,    rowLabel: "Level", labelTitle: "Ref", values: refCalConcValues  },
                        { key: "masterAlu",   label: "Master Curve ALU (MGCC)",            levels: MASTER_ALU_LEVELS, rowLabel: "Level", labelTitle: "Ref", values: masterAluValues   },
                        { key: "rccAlu",      label: "Reference Calibrator ALU (RCC)",     levels: RCC_ALU_LEVELS,    rowLabel: "Level", labelTitle: "Ref", values: rccAluValues      },
                        { key: "igccAlu",     label: "IGCC Calibrator ALU",                levels: IGCC_LEVELS,        rowLabel: "Cal",   labelTitle: "Cal", values: igccAluValues     },
                        { key: "igccConc",    label: "IGCC Calibrator Derived Conc",       levels: IGCC_LEVELS,        rowLabel: "Cal",   labelTitle: "Cal", values: igccConcValues    }
                    ].map(sec => (
                        <React.Fragment key={sec.key}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mt: 1 }}>{sec.label}</Typography>
                            <AssaySectionGrid sectionKey={sec.key} levels={sec.levels} assayNames={assayNames} sectionValues={sec.values} rowLabel={sec.rowLabel} labelTitle={sec.labelTitle} onCellsChange={setAssayCellsBatch} colWidth={colWidth} />
                        </React.Fragment>
                    ))}

                    {/* Unknown sections with their own row controls */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Unknown ALU</Typography>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(-10)} disabled={unknownRowCount <= MIN_UNKNOWN_ROWS}>-10</Button>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(-1)} disabled={unknownRowCount <= MIN_UNKNOWN_ROWS}>-1</Button>
                        <TextField
                            type="number"
                            size="small"
                            value={unknownRowDraft}
                            onChange={e => setUnknownRowsDirect(Number(e.target.value) || MIN_UNKNOWN_ROWS)}
                            inputProps={{ min: MIN_UNKNOWN_ROWS, max: MAX_UNKNOWN_ROWS, style: { width: 52, textAlign: "center" } }}
                            sx={{ width: 80 }}
                        />
                        <Button size="small" variant="contained" color="primary" sx={{ fontSize: 11, py: 0.5 }} onClick={confirmUnknownRows}>Apply</Button>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(1)}>+1</Button>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(10)}>+10</Button>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>rows</Typography>
                    </Box>
                    <AssaySectionGrid sectionKey="unknownAlu" levels={unknownRowCount} assayNames={assayNames} sectionValues={unknownAluValues} rowLabel="#" labelTitle="#" onCellsChange={setAssayCellsBatch} colWidth={colWidth} />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>Unknown Conc</Typography>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(-10)} disabled={unknownRowCount <= MIN_UNKNOWN_ROWS}>-10</Button>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(-1)} disabled={unknownRowCount <= MIN_UNKNOWN_ROWS}>-1</Button>
                        <TextField
                            type="number"
                            size="small"
                            value={unknownRowDraft}
                            onChange={e => setUnknownRowsDirect(Number(e.target.value) || MIN_UNKNOWN_ROWS)}
                            inputProps={{ min: MIN_UNKNOWN_ROWS, max: MAX_UNKNOWN_ROWS, style: { width: 52, textAlign: "center" } }}
                            sx={{ width: 80 }}
                        />
                        <Button size="small" variant="contained" color="primary" sx={{ fontSize: 11, py: 0.5 }} onClick={confirmUnknownRows}>Apply</Button>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(1)}>+1</Button>
                        <Button size="small" variant="outlined" sx={{ minWidth: 0, px: 1, fontSize: 11 }} onClick={() => changeUnknownRows(10)}>+10</Button>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>rows (shared with ALU)</Typography>
                    </Box>
                    <AssaySectionGrid sectionKey="unknownConc" levels={unknownRowCount} assayNames={assayNames} sectionValues={unknownConcValues} rowLabel="#" labelTitle="#" onCellsChange={setAssayCellsBatch} colWidth={colWidth} />
                </Paper>
            </Collapse>

            <Button onClick={() => setRccOutputOpen(!rccOutputOpen)} startIcon={rccOutputOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mb: 1 }}>
                RCC Outputs
            </Button>
            <Collapse in={rccOutputOpen} unmountOnExit>
                <OutputPanel assays={assays} resultKey="rccResult" label="RCC" unknownRowCount={unknownRowCount} activeAssay={activePanelAssay} />
            </Collapse>

            {/* IGCC Outputs */}
            <Button onClick={() => setIgccOutputOpen(!igccOutputOpen)} startIcon={igccOutputOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mb: 1 }}>
                IGCC Outputs
            </Button>
            <Collapse in={igccOutputOpen} unmountOnExit>
                <OutputPanel assays={assays} resultKey="igccResult" label="IGCC" unknownRowCount={unknownRowCount} activeAssay={activePanelAssay} />
            </Collapse>

            {/* Chart preview */}
            <Button onClick={() => setChartOpen(!chartOpen)} startIcon={chartOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ mb: 1 }}>
                Chart Preview ({chartAssayData?.name || "n/a"})
            </Button>
            <Collapse in={chartOpen} unmountOnExit>
                <Paper sx={{ p: 2, mb: 2 }}>
                    {/* Assay selector for chart -- conditional dropdown for >=50 */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5, px: 0.5, py: 0.5, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                        <Typography variant="caption" sx={{ alignSelf: "center", mr: 1, color: "text.secondary", whiteSpace: "nowrap" }}>Chart assay:</Typography>

                        {assays.length < 25 ? (
                            // Button strip for <25 assays
                            assays.map((a, i) => {
                                const hasAny = a.rccResult.status !== "" || a.igccResult.status !== "";
                                return (
                                    <Button key={i} size="small"
                                        variant={i === chartAssay ? "contained" : "outlined"}
                                        disabled={!hasAny}
                                        onClick={() => setChartAssay(i)}
                                        sx={{ minWidth: 60, height: 24, p: 0, fontSize: 10, lineHeight: 1, textTransform: "none", opacity: hasAny ? 1 : 0.4 }}
                                        title={hasAny ? `View chart for ${a.name}` : `${a.name} has not been run yet`}>
                                        {a.name}
                                    </Button>
                                );
                            })
                        ) : (
                            // Dropdown for >=25 assays
                            <Box sx={{ position: "relative" }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => setChartAssaySearchOpen(!chartAssaySearchOpen)}
                                    sx={{ minWidth: 200, height: 26, p: 0.5, fontSize: 11 }}
                                >
                                    {`Selected: ${assays[chartAssay]?.name || "Select"}`} ?
                                </Button>
                                {chartAssaySearchOpen && (
                                    <Paper sx={{ position: "absolute", top: "100%", left: 0, zIndex: 1000, mt: 0.5, minWidth: 200, maxHeight: 400, overflowY: "auto" }}>
                                        <TextField
                                            autoFocus
                                            size="small"
                                            placeholder="Search by name or #..."
                                            value={chartAssaySearch}
                                            onChange={e => setChartAssaySearch(e.target.value)}
                                            sx={{ width: "100%", p: 1, "& .MuiOutlinedInput-root": { fontSize: 12 } }}
                                            onKeyDown={e => {
                                                if (e.key === "Escape") setChartAssaySearchOpen(false);
                                            }}
                                        />
                                        {filteredChartAssays.map((item) => (
                                            <Button
                                                key={item.idx}
                                                fullWidth
                                                variant={item.idx === chartAssay ? "contained" : "text"}
                                                disabled={!item.hasResults}
                                                onClick={() => { setChartAssay(item.idx); setChartAssaySearchOpen(false); setChartAssaySearch(""); }}
                                                sx={{ justifyContent: "flex-start", fontSize: 12, px: 1, py: 0.5, opacity: item.hasResults ? 1 : 0.5 }}
                                            >
                                                #{item.idx + 1}: {item.name}
                                            </Button>
                                        ))}
                                    </Paper>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 1.5 }}>
                        <TextField label="Chart Title" size="small" value={chartTitle} onChange={e => setChartTitle(e.target.value || "Dose Curve Response")} sx={{ minWidth: 220 }} />
                        <TextField label="X Axis Label" size="small" value={xAxisLabel} onChange={e => setXAxisLabel(e.target.value || "Concentration")} sx={{ minWidth: 180 }} />
                        <TextField label="Y Axis Label" size="small" value={yAxisLabel} onChange={e => setYAxisLabel(e.target.value || "Signal (ALU)")} sx={{ minWidth: 180 }} />
                    </Box>

                    {activeCurvePts.length === 0 && activeIgccCurvePts.length === 0 ? (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Run Calculate RCC or Calculate IGCC on any assay, then select it above to view its chart.</Typography>
                    ) : (
                        <Box sx={{ width: 874, maxWidth: "100%", height: 540, position: "relative", border: "1px solid #ccc", borderRadius: 1, backgroundColor: "#fafafa", p: 1 }}>
                            {/* Legend */}
                            <Box sx={{ position: "absolute", top: 6, left: 10, zIndex: 3, display: "flex", gap: 1.5, pointerEvents: "none" }}>
                                {activeCurvePts.length > 0 && <Box sx={{ backgroundColor: "rgba(25,118,210,0.08)", color: "#1976d2", border: "1px solid rgba(25,118,210,0.3)", borderRadius: 1, px: 1, py: 0.25, fontSize: 11, fontWeight: 600 }}>RCC fit: {chartAssayData?.name}</Box>}
                                {activeIgccCurvePts.length > 0 && <Box sx={{ backgroundColor: "rgba(123,31,162,0.08)", color: "#7b1fa2", border: "1px solid rgba(123,31,162,0.3)", borderRadius: 1, px: 1, py: 0.25, fontSize: 11, fontWeight: 600 }}>IGCC fit{chartAssayData?.igccResult.igccApplied ? " (bent)" : ""}: {chartAssayData?.name}</Box>}
                            </Box>
                            <svg width="100%" height="100%" viewBox="0 0 874 540" preserveAspectRatio="none">
                                {(() => {
                                    const all = [...activeCurvePts, ...activeIgccCurvePts, ...activeRawPts, ...activeIgccCalPts];
                                    if (all.length === 0) return null;
                                    const xs = all.map(p => p.x), ys = all.map(p => p.y);
                                    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
                                    const leftPad = 70, rightPad = 20, topPad = 60, bottomPad = 50;
                                    const cw = 874 - leftPad - rightPad, ch = 540 - topPad - bottomPad;
                                    const sxf = x => leftPad + ((x - minX) / ((maxX - minX) || 1)) * cw;
                                    const syf = y => topPad + ch - ((y - minY) / ((maxY - minY) || 1)) * ch;
                                    const xTicks = Array.from({ length: 5 }, (_, i) => { const v = minX + ((maxX - minX) * i) / 4; return { v, x: sxf(v) }; });
                                    const yTicks = Array.from({ length: 5 }, (_, i) => { const v = minY + ((maxY - minY) * (4 - i)) / 4; return { v, y: syf(v) }; });
                                    return (
                                        <>
                                            <text x={leftPad + cw / 2} y="42" text-anchor="middle" font-size="14" font-weight="bold" fill="#333">{chartTitle}</text>
                                            <line x1={leftPad} y1={topPad + ch} x2={leftPad + cw} y2={topPad + ch} stroke="#555" />
                                            <line x1={leftPad} y1={topPad} x2={leftPad} y2={topPad + ch} stroke="#555" />
                                            {xTicks.map((t, i) => <g key={i}><line x1={t.x} y1={topPad + ch} x2={t.x} y2={topPad + ch + 5} stroke="#555" /><text x={t.x} y={topPad + ch + 17} text-anchor="middle" font-size="10" fill="#333">{Number(t.v).toFixed(2)}</text></g>)}
                                            {yTicks.map((t, i) => <g key={i}><line x1={leftPad - 5} y1={t.y} x2={leftPad} y2={t.y} stroke="#555" /><line x1={leftPad} y1={t.y} x2={leftPad + cw} y2={t.y} stroke="#e0e0e0" /><text x={leftPad - 8} y={t.y + 3} text-anchor="end" font-size="10" fill="#333">{Number(t.v).toFixed(2)}</text></g>)}
                                            <text x={leftPad + cw / 2} y={topPad + ch + 40} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333">{xAxisLabel}</text>
                                            <text x="12" y={topPad + ch / 2} textAnchor="middle" fontSize="12" fontWeight="bold" fill="#333" transform={`rotate(-90 12 ${topPad + ch / 2})`}>{yAxisLabel}</text>
                                            {/* RCC curve - blue */}
                                            {activeCurvePts.length > 0 && <polyline fill="none" stroke="#1976d2" strokeWidth="2" points={activeCurvePts.map(p => `${sxf(p.x)},${syf(p.y)}`).join(" ")} />}
                                            {activeCurvePts.map((p, i) => <circle key={i} cx={sxf(p.x)} cy={syf(p.y)} r={3} fill="#1976d2" stroke="white" strokeWidth={1.5} style={{ cursor: "pointer" }} onMouseEnter={() => setHoveredPoint({ type: "RCC Curve", x: p.x, y: p.y, svgX: sxf(p.x), svgY: syf(p.y) })} onMouseLeave={() => setHoveredPoint(null)} />)}
                                            {/* RCC raw data - red dots */}
                                            {activeRawPts.map((p, i) => <circle key={i} cx={sxf(p.x)} cy={syf(p.y)} r={4} fill="#d32f2f" style={{ cursor: "pointer" }} onMouseEnter={() => setHoveredPoint({ type: "RCC Raw", x: p.x, y: p.y, svgX: sxf(p.x), svgY: syf(p.y) })} onMouseLeave={() => setHoveredPoint(null)} />)}
                                            {/* IGCC curve - purple dashed */}
                                            {activeIgccCurvePts.length > 0 && <polyline fill="none" stroke="#7b1fa2" strokeWidth="2" strokeDasharray="6 3" points={activeIgccCurvePts.map(p => `${sxf(p.x)},${syf(p.y)}`).join(" ")} />}
                                            {activeIgccCurvePts.map((p, i) => <circle key={i} cx={sxf(p.x)} cy={syf(p.y)} r={3} fill="#7b1fa2" stroke="white" strokeWidth={1.5} style={{ cursor: "pointer" }} onMouseEnter={() => setHoveredPoint({ type: "IGCC Curve", x: p.x, y: p.y, svgX: sxf(p.x), svgY: syf(p.y) })} onMouseLeave={() => setHoveredPoint(null)} />)}
                                            {/* IGCC calibrator points - purple crosshairs */}
                                            {activeIgccCalPts.map((p, i) => { const cx = sxf(p.x), cy = syf(p.y), arm = 7; return <g key={i} style={{ cursor: "pointer" }} onMouseEnter={() => setHoveredPoint({ type: "IGCC Cal", x: p.x, y: p.y, svgX: cx, svgY: cy })} onMouseLeave={() => setHoveredPoint(null)}><line x1={cx - arm} y1={cy} x2={cx + arm} y2={cy} stroke="#7b1fa2" strokeWidth="2.5" strokeLinecap="round" /><line x1={cx} y1={cy - arm} x2={cx} y2={cy + arm} stroke="#7b1fa2" strokeWidth="2.5" strokeLinecap="round" /></g>; })}
                                            {hoveredPoint && <><line x1={sxf(hoveredPoint.x)} y1={topPad} x2={sxf(hoveredPoint.x)} y2={topPad + ch} stroke="#333" strokeWidth={1} strokeDasharray="4 4" /><line x1={leftPad} y1={syf(hoveredPoint.y)} x2={leftPad + cw} y2={syf(hoveredPoint.y)} stroke="#333" strokeWidth={1} strokeDasharray="4 4" /></>}
                                        </>
                                    );
                                })()}
                            </svg>
                            {hoveredPoint && (
                                <Box sx={{ position: "absolute", left: hoveredPoint.svgX, top: hoveredPoint.svgY, transform: "translate(10px, -110%)", backgroundColor: "#222", color: "#fff", px: 1.25, py: 0.75, borderRadius: 1, fontSize: 12, pointerEvents: "none", boxShadow: 3, whiteSpace: "nowrap", zIndex: 2 }}>
                                    <div>{hoveredPoint.type}</div>
                                    <div>{xAxisLabel}: {Number(hoveredPoint.x).toFixed(4)}</div>
                                    <div>{yAxisLabel}: {Number(hoveredPoint.y).toFixed(4)}</div>
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>
            </Collapse>

            <Typography
                variant="caption"
                sx={{
                    display: "block",
                    mt: 3,
                    pt: 2,
                    borderTop: "1px solid #eee",
                    color: "text.secondary",
                    fontSize: 11
                }}
            >
                Frontend v{APP_VERSION} {"\u2022"} Math library {MATH_LIB_VERSION}
            </Typography>
        </Box>
    );
};

export default G06MultiAssay;
